export {FALLBACK_QUESTION_POOL} from './fallbackQuestions'

// ── LocalStorage keys ───────────────────────────────────────────────────────

export const STORAGE_KEYS = {
  PLAYER_ID: 'fraud-game-player-id',
  EMAIL: 'fraud-game-email',
  USERNAME: 'fraud-game-username',
} as const

// ── External integrations ───────────────────────────────────────────────────

export const HOTJAR_ID = 3027960
export const HOTJAR_SV = 6

// ── Audio ───────────────────────────────────────────────────────────────────

export const AUDIO_VOLUME = 0.3

// ── Transitions ─────────────────────────────────────────────────────────────

export const SCREEN_FADE_MS = 400

// ── Scoring ─────────────────────────────────────────────────────────────────

export const MAX_TIME_BONUS = 20

// ── Timer badge thresholds ──────────────────────────────────────────────────

export const TIMER_LOW_THRESHOLD = 5
export const TIMER_CRITICAL_THRESHOLD = 3

export const TIMER_COLORS = {
  normal: '#FAE487',
  low: '#F4721B',
  critical: '#ef4444',
} as const

// ── Legal URLs ──────────────────────────────────────────────────────────────

export const GENERAL_TERMS_URL =
  'https://getmoss.com/public/terms-and-conditions/20250101_INT-en_Nufin_GmbH_Terms_and_Conditions_v11.0.pdf'

export const GAME_TERMS_URL = '/public/terms-and-conditions/20260415_INT-en_Promotion_TC_v1.0.pdf'

export const GAME_RULES_URL = '/public/terms-and-conditions/20260429_INT-en_Promotion_Rules_v1.0_Papertrails.pdf'

export const PERSONAS: Array<{
  minScore: number
  title: string
  subline: string
  cardImage: string
  badgeImage: string
}> = [
  {
    minScore: 80,
    title: 'The Final Approver',
    subline: 'Nothing moves without your sign-off.',
    cardImage: '/images/fraud-game/score-cards/card-finalApprover.png',
    badgeImage: '/images/fraud-game/badges/badge-finalApprover.png',
  },
  {
    minScore: 60,
    title: 'The Paper Trail Hunter',
    subline: "You follow the money, it doesn't get far.",
    cardImage: '/images/fraud-game/score-cards/card-paperTrailHunter.png',
    badgeImage: '/images/fraud-game/badges/badge-paperTrailHunter.png',
  },
  {
    minScore: 40,
    title: 'The Accrual Suspect',
    subline: "Something doesn't add up — and it might be you.",
    cardImage: '/images/fraud-game/score-cards/card-accrualSuspect.png',
    badgeImage: '/images/fraud-game/badges/badge-accrualSuspect.png',
  },
  {
    minScore: 20,
    title: 'The Receipt Ghost',
    subline: "The paperwork exists. You just can't find it.",
    cardImage: '/images/fraud-game/score-cards/card-receiptGhost.png',
    badgeImage: '/images/fraud-game/badges/badge-receiptGhost.png',
  },
  {
    minScore: 0,
    title: 'The Auto Approver',
    subline: 'Approved everything, questioned nothing.',
    cardImage: '/images/fraud-game/score-cards/card-autoApprover.png',
    badgeImage: '/images/fraud-game/badges/badge-autoApprover.png',
  },
]

export const TOTAL_QUESTIONS = 8

export const SCORE_CARD_RATING_LABELS = {
  strong: 'Strong',
  developing: 'Developing',
  'needs-work': 'Needs work',
}

export const SCORE_CARD_RATING_COLORS = {
  strong: '#C6FF00',
  developing: '#F4721B',
  'needs-work': '#EA4949',
}

export const TAGLINE_TEXT = 'Can you spot what others miss?'

export const GAME_STYLES = `
.font-typewriter {
  font-family: 'American Typewriter', 'Courier New', serif;
}
@keyframes pin-in {
  0% { transform: scale(0) rotate(-15deg); }
  100% { transform: scale(1) rotate(-15deg); }
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes ghost-shimmer {
  0% { opacity: 0.3; }
  50% { opacity: 0.6; }
  100% { opacity: 0.3; }
}
@keyframes fade-in {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes fade-up {
  0% { opacity: 0; transform: translateY(24px); }
  70% { opacity: 1; transform: translateY(-3px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -50% 0; }
}
@keyframes slide-behind {
  0% { transform: translateX(0); }
  100% { transform: translateX(-173px); }
}
@keyframes stamp-pop {
  0% { transform: scale(2.5) rotate(-12deg); opacity: 0; }
  60% { transform: scale(0.9) rotate(-12deg); opacity: 1; }
  100% { transform: scale(1) rotate(-12deg); opacity: 1; }
}
`

export const LOADING_LINES: Array<{text: string; keyword: string}> = [
  {text: 'Collecting evidence.', keyword: 'evidence'},
  {text: 'Analysing your instincts.', keyword: 'instincts'},
  {text: 'Preparing your verdict.', keyword: 'verdict'},
]

// ── Shared button styles ─────────────────────────────────────────────────────

export const BTN_PRIMARY =
  'rounded-[4px] border border-green-neon bg-green-neon px-6 py-2.5 text-13 font-medium uppercase text-moss-black transition-all hover:bg-transparent hover:text-green-neon disabled:opacity-40 disabled:hover:bg-green-neon disabled:hover:text-moss-black'

export const BTN_SECONDARY =
  'rounded-[4px] border border-moss-white bg-transparent px-6 py-2.5 text-13 font-semibold uppercase text-moss-white transition-all hover:bg-moss-white hover:text-moss-black'

// ── Animation timing ─────────────────────────────────────────────────────────

export const CHAR_SPEED = 65
export const POST_TYPE_WAIT = 350
export const REDACT_DURATION = 250
export const POST_REDACT_WAIT = 200

export const PHASE_TRANSITION_HOLD = 1000

export const PHASE_TRANSITION_CONFIGS: Record<
  1 | 2,
  {completion: string; title: string; subtitle: string; caseFileLabel: string}
> = {
  1: {
    completion: 'FIELD NOTES LOGGED.',
    title: 'Examine the evidence',
    subtitle: 'The details matter now. Look closely.',
    caseFileLabel: 'Case File 01',
  },
  2: {
    completion: 'EVIDENCE EXAMINED.',
    title: 'Close the case',
    subtitle: 'This is where most investigators slip up.',
    caseFileLabel: 'Case File 02',
  },
}
